import React, { useEffect, useState } from 'react';
import { Box } from '@abyss/web/ui/Box';
import { Label } from '@abyss/web/ui/Label';
import { Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { API_URL } from '@src/constants/BaseUrls';
import { CookieService } from '@src/api/common/CookieService';
import { Breadcrumbs, Title } from './PageInfo';

interface Template {
  name: string;
  description: string;
  filename: string;
  templateKey: 'clinic' | 'warehouse' | 'user';
  icon: string;
  required: boolean;
  color: string;
  columns: string[];
  sampleData: string[][];
}

const TEMPLATES: Template[] = [
  {
    name: 'Clinic Site Template',
    description:
      'Template for uploading clinic site locations. This file is required for processing.',
    filename: 'clinic_site_template.csv',
    templateKey: 'clinic',
    icon: 'local_hospital',
    required: true,
    color: '#EAF2FF',
    columns: ['Street Address', 'City', 'State', 'Zip Code', 'Site Code'],
    sampleData: [
      ['123 Main St', 'Boston', 'MA', '02101', 'CLINIC-001'],
      ['456 Oak Ave', 'Cambridge', 'MA', '02139', 'CLINIC-002'],
      ['789 Elm Street', 'Somerville', 'MA', '02144', 'CLINIC-003'],
    ],
  },
  {
    name: 'Warehouse Template',
    description:
      'Template for uploading warehouse locations. Optional but improves distribution planning.',
    filename: 'warehouse_template.csv',
    templateKey: 'warehouse',
    icon: 'warehouse',
    required: false,
    color: '#fff8e1',
    columns: ['Street Address', 'City', 'State', 'Zip Code', 'Site Code'],
    sampleData: [
      ['100 Warehouse Blvd', 'Boston', 'MA', '02101', 'WH-001'],
      ['200 Distribution Dr', 'Worcester', 'MA', '01608', 'WH-002'],
    ],
  },
  {
    name: 'Remote User Template',
    description:
      'Template for uploading remote user home locations. Optional but enables user assignment.',
    filename: 'remote_user_template.csv',
    templateKey: 'user',
    icon: 'person_pin_circle',
    required: false,
    color: '#e8f5e9',
    columns: ['Person Name', 'Street Address', 'City', 'State', 'Zip Code'],
    sampleData: [
      ['John Doe', '10 Home St', 'Boston', 'MA', '02101'],
      ['Jane Smith', '20 Residence Ln', 'Cambridge', 'MA', '02139'],
      ['Bob Johnson', '30 Remote Rd', 'Brookline', 'MA', '02445'],
    ],
  },
];



const normalizeBaseUrl = (baseUrl?: string) => {
  if (!baseUrl) {
    return '';
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const resolveRouteToolApiBase = () => {
  const configured = normalizeBaseUrl(API_URL);
  if (!configured || configured.includes('xyz-dev.com')) {
    return '';
  }
  return configured;
};

const buildApiUrl = (baseUrl: string, path: string) => {
  if (baseUrl.endsWith('/api') && path.startsWith('/api/')) {
    return `${baseUrl}${path.slice(4)}`;
  }
  return `${baseUrl}${path}`;
};

const getAuthHeaders = (): HeadersInit => {
  const token = CookieService.GetCookie('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const downloadBlobFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};



export const TemplatesScreen = () => {
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const baseUrl = resolveRouteToolApiBase();
  const [downloadingTemplates, setDownloadingTemplates] = useState(false);

  useEffect(() => {
    (async () => {
      await UpdatePageTitle(Title);
      await UpdateBreadcrumb(Breadcrumbs);
    })();
  }, []);

  const downloadTemplateFromApi = async (template: Template) => {
    const response = await fetch(
      buildApiUrl(baseUrl, `/api/templates/${template.templateKey}`),
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Template download failed with ${response.status}`);
    }

    const blob = await response.blob();
    const extension =
      String(response.headers.get('content-type') || '').includes('sheet') ||
      String(response.headers.get('content-type') || '').includes('excel')
        ? 'xlsx'
        : 'csv';
    const filenameBase = template.filename.replace(/\.[a-z0-9]+$/i, '');
    downloadBlobFile(blob, `${filenameBase}.${extension}`);
  };

  const downloadTemplate = (template: Template) => {
    void (async () => {
      try {
        await downloadTemplateFromApi(template);
      } catch {
        // Fallback to generated CSV sample when template API is unavailable.
        const csvContent = [
          template.columns.join(','),
          ...template.sampleData.map((row) => row.join(',')),
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        downloadBlobFile(blob, template.filename);
      }
    })();
  };

  const downloadAllTemplates = () => {
    void (async () => {
      setDownloadingTemplates(true);
      try {
        for (const template of TEMPLATES) {
          await downloadTemplateFromApi(template);
        }
      } catch {
        // Fall back silently per-template if API is unavailable.
        TEMPLATES.forEach((template) => {
          const csvContent = [
            template.columns.join(','),
            ...template.sampleData.map((row) => row.join(',')),
          ].join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv' });
          downloadBlobFile(blob, template.filename);
        });
      } finally {
        setDownloadingTemplates(false);
      }
    })();
  };



  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {/* Outer wrapper card */}
      <Card
        css={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
        }}
      >
        <Card.Section>
          {/* Download All button row */}
          <Box
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '12px',
            }}
          >
            <Button
              size="sm"
              onClick={downloadAllTemplates}
              isLoading={downloadingTemplates}
              ariaLoadingLabel="Downloading all templates"
            >
              <IconSymbol icon="download" size={14} />
              Download All Templates
            </Button>
          </Box>

          {/* Template Cards Grid */}
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '16px',
              alignItems: 'stretch',
              '@media (max-width: 1024px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
              '@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
            }}
          >
            {TEMPLATES.map((template) => (
            <Box
                key={template.name}
                css={{
                  border: '1px solid #d9e0eb',
                  borderRadius: '8px',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#ffffff',
                  height: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  css={{
                    backgroundColor: template.color,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    marginBottom: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', minWidth: 0 }}>
                    <IconSymbol
                      icon={template.icon as any}
                      size={20}
                      color="$primary1"
                      variant="filled"
                    />
                    <Label fontWeight="bold" size="sm" color="$gray8" css={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {template.name}
                    </Label>
                    {template.required ? (
                      <span
                        style={{
                          padding: '2px 8px',
                          backgroundColor: '#196ECF',
                          borderRadius: '999px',
                          flexShrink: 0,
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#ffffff',
                          lineHeight: '16px',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        Required
                      </span>
                    ) : (
                      <span
                        style={{
                          padding: '2px 8px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '999px',
                          flexShrink: 0,
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#888888',
                          lineHeight: '16px',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        Optional
                      </span>
                    )}
                  </div>
                </Box>

                {/* Description */}
                <Label
                  size="xs"
                  color="$gray6"
                  css={{
                    marginBottom: '8px',
                    display: 'block',
                    lineHeight: '1.4',
                    minHeight: '28px',
                  }}
                >
                  {template.description}
                </Label>

                {/* Columns */}
                <Label
                  size="xs"
                  color="$gray5"
                  fontWeight="bold"
                  css={{
                    marginBottom: '4px',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Columns
                </Label>
                <Layout.Group
                  space={4}
                  css={{ marginBottom: '8px', flexWrap: 'wrap' }}
                >
                  {template.columns.map((col) => (
                    <Box
                      key={col}
                      css={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#f0f4ff',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Label size="xs" color="$gray7">
                        {col}
                      </Label>
                    </Box>
                  ))}
                </Layout.Group>

                {/* File Info + Download on same row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    marginTop: 'auto',
                    paddingTop: '10px',
                    borderTop: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, flex: 1 }}>
                    <IconSymbol icon="description" size={12} color="$gray5" style={{ flexShrink: 0 }} />
                    <Label
                      size="xs"
                      color="$gray5"
                      css={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        display: 'block',
                      }}
                    >
                      {template.filename}
                    </Label>
                  </div>
                  <Button size="sm" onClick={() => downloadTemplate(template)} css={{ flexShrink: 0 }}>
                    <IconSymbol icon="download" size={12} />
                    Download
                  </Button>
                </div>
              </Box>
            ))}
          </Box>
        </Card.Section>
      </Card>
    </Box>
  );
};
